export class MapCursor {
    constructor(container) {
        this.container = container;
        this.bounds = null;
        this.active = false;
        this.isHovered = false;
        this.isVisible = false;
        this.cursorText = "SELECT TO EXPLORE INFO - SELECT TO EXPLORE INFO - ";
        this.radius = 70;
        this.rotation = 0;
        this.rotationSpeed = 0.5;
        this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

        this.elements = {
            container: document.createElement('div'),
            dot: document.createElement('div'),
            textRing: document.createElement('div')
        };

        this.position = {
            dot: { x: 0, y: 0 },
            circle: { x: 0, y: 0 },
            target: { x: 0, y: 0 }
        };

        this.velocity = { x: 0, y: 0 };
        this.onStateChange = null;

        this.textOpacity = 0;
        this.textScale = 0.3;
        this.dotScale = 0;
        this.targetTextOpacity = 1;
        this.targetTextScale = 1;
        this.targetDotScale = 1;

        this.init();
    }

    init() {
        this.elements.container.className = 'interactive-cursor';
        this.elements.dot.className = 'cursor-dot';
        this.elements.textRing.className = 'cursor-text-ring';

        const chars = this.cursorText.split('');
        const angleStep = (Math.PI * 2) / chars.length;

        chars.forEach((char, i) => {
            const charElement = document.createElement('div');
            charElement.className = 'char';
            charElement.textContent = char;

            const angle = angleStep * i;
            const x = Math.cos(angle) * this.radius;
            const y = Math.sin(angle) * this.radius;

            const rotation = (angle * 180 / Math.PI) + 90;
            charElement.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) rotate(${rotation}deg)`;

            this.elements.textRing.appendChild(charElement);
        });

        this.elements.container.appendChild(this.elements.dot);
        this.elements.container.appendChild(this.elements.textRing);
        this.container.appendChild(this.elements.container);

        this.container.addEventListener('mouseenter', this.onMouseEnter.bind(this));
        this.container.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }

    onMouseEnter(event) {
        if (this.isTouchDevice) return;

        this.active = true;
        this.isVisible = true;
        this.elements.container.classList.add('visible');
        this.position.circle.x = event.clientX;
        this.position.circle.y = event.clientY;
        this.position.dot.x = event.clientX;
        this.position.dot.y = event.clientY;
        this.position.target.x = event.clientX;
        this.position.target.y = event.clientY;
        this.textOpacity = 0;
        this.textScale = 0.3;
        this.dotScale = 0;
        this.targetTextOpacity = 1;
        this.targetTextScale = 1;
        this.targetDotScale = 1;
        if (this.onStateChange) this.onStateChange(true);
    }

    onMouseLeave() {
        if (this.isTouchDevice) return;

        this.isVisible = false;
        this.targetTextOpacity = 0;
        this.targetTextScale = 0.3;
        this.targetDotScale = 0;
        this.setHoverState(false);
        if (this.onStateChange) this.onStateChange(false);
    }

    lerp(start, end, t) {
        return start * (1 - t) + end * t;
    }

    show(event) {
        if (this.isTouchDevice) return;

        if (!this.active) {
            this.active = true;
            this.isVisible = true;
            this.elements.container.classList.add('visible');
            this.position.circle.x = event.clientX;
            this.position.circle.y = event.clientY;
            this.position.dot.x = event.clientX;
            this.position.dot.y = event.clientY;
            this.position.target.x = event.clientX;
            this.position.target.y = event.clientY;
            this.textOpacity = 0;
            this.textScale = 0.3;
            this.dotScale = 0;
            this.targetTextOpacity = 1;
            this.targetTextScale = 1;
            this.targetDotScale = 1;
            if (this.onStateChange) this.onStateChange(true);
        }
    }

    hide() {
        if (this.isTouchDevice) return;

        if (this.isVisible) {
            this.isVisible = false;
            this.targetTextOpacity = 0;
            this.targetTextScale = 0.3;
            this.targetDotScale = 0;
        }
    }

    onMouseMove(event) {
        if (this.isTouchDevice) return;

        this.bounds = this.container.getBoundingClientRect();
        const isWithinBounds =
            event.clientX >= this.bounds.left &&
            event.clientX <= this.bounds.right &&
            event.clientY >= this.bounds.top &&
            event.clientY <= this.bounds.bottom;

        if (isWithinBounds) {
            this.show(event);
        } else {
            this.hide();
        }

        if (this.active) {
            this.position.target.x = event.clientX;
            this.position.target.y = event.clientY;
        }
    }

    update() {
        if (this.isTouchDevice) return;
        if (!this.active) return;

        this.position.dot.x += (this.position.target.x - this.position.dot.x) * 0.5;
        this.position.dot.y += (this.position.target.y - this.position.dot.y) * 0.5;

        this.velocity.x = this.position.dot.x - this.position.circle.x;
        this.velocity.y = this.position.dot.y - this.position.circle.y;

        const circleEase = 0.1;
        this.position.circle.x += this.velocity.x * circleEase;
        this.position.circle.y += this.velocity.y * circleEase;

        this.textOpacity = this.lerp(this.textOpacity, this.targetTextOpacity, 0.1);
        this.textScale = this.lerp(this.textScale, this.targetTextScale, 0.1);
        this.dotScale = this.lerp(this.dotScale, this.targetDotScale, 0.1);

        this.rotation = (this.rotation + this.rotationSpeed) % 360;

        this.elements.dot.style.transform = `translate(${this.position.dot.x}px, ${this.position.dot.y}px) translate(-50%, -50%) scale(${this.dotScale})`;

        this.elements.textRing.style.transform = `translate(${this.position.circle.x}px, ${this.position.circle.y}px) translate(-50%, -50%) rotate(${this.rotation}deg) scale(${this.textScale})`;
        this.elements.textRing.style.opacity = this.textOpacity;

        if (!this.isVisible && this.textOpacity < 0.01) {
            this.active = false;
            this.elements.container.classList.remove('visible');
            if (this.onStateChange) this.onStateChange(false);
        }
    }

    setHoverState(isHovered) {
        if (this.isTouchDevice) return;

        this.isHovered = isHovered;
        if (this.isVisible) {
            this.targetTextOpacity = isHovered ? 0 : 1;
            this.targetTextScale = isHovered ? 0.3 : 1;
            this.targetDotScale = isHovered ? 2 : 1;
        }
    }

    onResize() {
        if (this.active) {
            this.bounds = this.container.getBoundingClientRect();
        }
    }

    destroy() {
        this.container.removeEventListener('mouseenter', this.onMouseEnter.bind(this));
        this.container.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
        this.elements.container.remove();
    }
}
